import { type ab_media_request as ddcAbMediaRequest } from "ddc";
import * as uuid from "uuid";
import { useApiCore } from "contexts";
import { type PublishMediaDataRoomInput } from "features/mediaDataRoom/models";
import { normalizeEmail } from "utils/String";
import {
  calculateAttestationHash,
  retrieveLatestMediaEnclaveSpecifications,
} from "wrappers/ApolloWrapper/resolvers/media";

const usePublishMediaDataRoom = () => {
  const { client: apiCoreClient, sessionManager } = useApiCore();

  const publish = async (
    input: PublishMediaDataRoomInput
  ): Promise<{
    id: string;
    driverAttestationHash: string;
  }> => {
    const enclaveSpecifications =
      await retrieveLatestMediaEnclaveSpecifications(apiCoreClient);
    const driverAttestationHash = calculateAttestationHash(
      enclaveSpecifications.driverEnclaveSpecification
    );
    const [authenticationRootCertificatePem, session] = await Promise.all([
      apiCoreClient.decentriqCaRootCertificate(),
      sessionManager.get({ driverAttestationHash }),
    ]);
    const dataRoom = session.compiler.abMedia.create({
      v6: {
        advertiserEmails: input.advertiserEmails.map(normalizeEmail),
        agencyEmails: input.agencyEmails.map(normalizeEmail),
        authenticationRootCertificatePem: new TextDecoder().decode(
          authenticationRootCertificatePem
        ),
        dataPartnerEmails: input.dataPartnerEmails.map(normalizeEmail),
        driverEnclaveSpecification:
          enclaveSpecifications.driverEnclaveSpecification,
        enableAdvertiserAudienceDownload:
          input?.enableAdvertiserAudienceDownload,
        enableDataPartner: input.dataPartnerEmails.length > 0,
        enableDebugMode: false,
        enableHideAbsoluteValuesFromInsights:
          input?.hideAbsoluteValuesFromInsights,
        enableInsights: input?.enableInsights,
        enableLookalikeAudiences: input?.enableLookalike,
        enableRemarketing: input?.enableRemarketing,
        enableRuleBasedAudiences: input?.enableRuleBasedAudiences,
        hashMatchingIdWith: input.matchingIdHashingAlgorithm,
        id: uuid.v4(),
        mainAdvertiserEmail: normalizeEmail(input.mainAdvertiserEmail),
        mainPublisherEmail: normalizeEmail(input.mainPublisherEmail),
        matchingIdFormat: input.matchingIdFormat,
        modelEvaluation: {
          postScopeMerge: ["ROC_CURVE"],
          preScopeMerge: [],
        },
        name: input.name,
        observerEmails: input.observerEmails.map(normalizeEmail),
        publisherEmails: input.publisherEmails.map(normalizeEmail),
        pythonEnclaveSpecification:
          enclaveSpecifications.pythonEnclaveSpecification,
      },
    });
    const request: ddcAbMediaRequest.AbMediaRequest = {
      publishDataRoom: {
        dataRoom,
        requirePassword: false,
        showOrganizationLogo: false,
      },
    };
    const resp = await session.sendAbMediaRequest(request);
    if (!("publishDataRoom" in resp)) {
      throw new Error("Expected publishDataRoom response");
    }
    return {
      driverAttestationHash,
      id: resp?.publishDataRoom.dataRoomId,
    };
  };
  return {
    publish,
  };
};

export default usePublishMediaDataRoom;
